import {
  useState,
  forwardRef,
  useImperativeHandle,
  Ref,
  ForwardedRef,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import { useAsync, useLatest } from 'react-use'
import { filter, find, isNull, isUndefined } from 'lodash-es'
import { Box } from '@mui/material'
import WebViewer, { WebViewerInstance, WebViewerOptions } from '@pdftron/webviewer'
import { appConfig } from 'src/constants'
import ViewerPlaceholders from './components/ViewerPlaceholders'
import { PDFAConformance, ViewerDocument, ViewerMimeTypes } from './const'
import {
  applyPageExclusionPreview,
  createBringToBackAction,
  createBringToFrontAction,
  createCopyEvents,
  exportDocumentAnnotations,
} from './utils'

const emptyArray: AdditionalShortcut[] = []

const fileTypeToExtension: Record<ViewerMimeTypes, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/octet-stream': 'doc',
}

interface AdditionalShortcut {
  key: string
  action: () => void
}

interface Props {
  /**
   * Url that should be loaded in the viewer.
   */
  document: ViewerDocument | null
  /**
   * Indicates whether user has admin rights for the viewer. Cannot be changed
   * after initialization.
   */
  isAdmin?: boolean
  additionalShortcuts?: AdditionalShortcut[]
  viewerHeight: string | number
  /**
   * Callback that will be called when the document is changed. (annotation change has been triggered)
   * @returns
   */
  onChangeDocument?: () => void
  onDocumentLoaded?: (instance: WebViewerInstance) => void

  croppedArea?: {
    croppedLeft: number
    croppedTop: number
    croppedRight: number
    croppedBottom: number
  }
  /** 1-based pages to mark as excluded from the Statikdokument */
  excludedPages?: number[]
  /** Hides the viewer's own download, "Speichern unter" and print actions. */
  hideFileActions?: boolean
  /**
   * Called when the user adds, changes or deletes an annotation. Unlike `onChangeDocument` (any
   * click or key press), this ignores annotations imported while a document loads.
   */
  onAnnotationsChanged?: () => void
  /** Shown instead of the default hint while no document is loaded */
  emptyState?: ReactNode
}

export interface ViewerRef {
  /**
   * Get a blob of the current loaded document
   * @returns
   */
  getBlobCurrentDocument: () => Promise<Blob | null>
  /**
   * Pass a url to a pdf and get a blob of the pdf/a version of the pdf.
   * @param pdfUlr
   * @returns
   */
  getBlobPDFA: (
    pdfUlr: string,
    conformance: PDFAConformance,
  ) => Promise<{ blob: Blob; errors: string[] } | null>

  viewerInstance: WebViewerInstance | null
  /** Reloads the current document from its url, discarding unsaved annotation changes. */
  reloadDocument: () => void
}

const Viewer = forwardRef(
  (
    {
      document,
      isAdmin = true,
      additionalShortcuts = emptyArray,
      viewerHeight,
      onChangeDocument,
      onDocumentLoaded,
      croppedArea,
      excludedPages,
      hideFileActions = false,
      onAnnotationsChanged,
      emptyState,
    }: Props,
    ref: ForwardedRef<ViewerRef>,
  ) => {
    const [viewerRef, setViewerRef] = useState<HTMLDivElement | null>(null)

    const viewerInstanceState = useAsync(async () => {
      if (isNull(viewerRef) || isUndefined(viewerRef)) return

      try {
        const options: WebViewerOptions = {
          path: '/webviewer',
          licenseKey: appConfig.apryseLicenceKey as string,
          isAdminUser: isAdmin,
          enableMeasurement: true,
          fullAPI: true,
        }

        const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)
        const instance = isFirefox
          ? await WebViewer.WebComponent(options, viewerRef)
          : await WebViewer.Iframe(options, viewerRef)

        return instance
      } catch (err: unknown) {
        return err instanceof Error ? err.message : 'Unknown error'
      }
    }, [viewerRef])

    useEffect(() => {
      const instance = viewerInstanceState.value
      return () => {
        if (instance && typeof instance !== 'string') {
          instance.UI.dispose()
        }
      }
    }, [viewerInstanceState.value])

    const viewerInstance =
      typeof viewerInstanceState.value === 'string' ? null : viewerInstanceState.value ?? null

    const viewerInstanceError =
      typeof viewerInstanceState.value === 'string' ? viewerInstanceState.value : null

    const isLoadingViewerInstance = viewerInstanceState.loading

    const [error, setError] = useState<string | null>(null)
    const [documentLoading, setDocumentLoading] = useState(false)
    const [loadedDocumentUrl, setLoadedDocumentUrl] = useState<string | null>(null)

    const croppedLeft = croppedArea?.croppedLeft
    const croppedTop = croppedArea?.croppedTop
    const croppedRight = croppedArea?.croppedRight
    const croppedBottom = croppedArea?.croppedBottom

    useEffect(() => {
      setDocumentLoading(false)
    }, [document])

    const applyCroppedArea = useCallback(
      (config: {
        croppedLeft: number
        croppedTop: number
        croppedRight: number
        croppedBottom: number
      }) => {
        if (viewerInstance) {
          const currentDoc = viewerInstance.Core.documentViewer.getDocument()

          if (!currentDoc) return

          // FIRST REMOVE ALL PREVIOUS CROP PREVIEW ANNOTATIONS
          if (
            !config ||
            (config.croppedLeft === 0 &&
              config.croppedTop === 0 &&
              config.croppedRight === 0 &&
              config.croppedBottom === 0)
          ) {
            try {
              const { annotationManager } = viewerInstance.Core
              const existingAnnotations = annotationManager.getAnnotationsList()

              const annotationsToDelete = filter(
                existingAnnotations,
                annotation => annotation.getCustomData('cropPreview') !== '',
              )

              annotationManager.deleteAnnotations(annotationsToDelete, { force: true })
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error removing crop preview:', error)
            }
            return
          }

          // CREATE NEW CROP PREVIEW ANNOTATIONS

          try {
            const { Core } = viewerInstance
            const { annotationManager } = Core

            const existingAnnotations = annotationManager.getAnnotationsList()
            const cropPreviewAnnotations = filter(
              existingAnnotations,
              annotation => annotation.getCustomData('cropPreview') !== '',
            )

            const documentViewer = Core.documentViewer
            const currentDocument = documentViewer.getDocument()

            if (!currentDocument) return

            // Get total number of pages
            const pageCount = currentDocument.getPageCount()

            // Convert millimeters to points (1 mm = 2.834645669 points)
            const mmToPoints = 2.834645669 * 1000

            // Create crop preview for each page
            for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
              const pageInfo = currentDocument.getPageInfo(pageNumber)
              const pageWidth = pageInfo.width
              const pageHeight = pageInfo.height

              // Calculate crop area coordinates
              const left = config.croppedLeft * mmToPoints
              const top = config.croppedTop * mmToPoints
              const right = pageWidth - config.croppedRight * mmToPoints
              const bottom = pageHeight - config.croppedBottom * mmToPoints

              const existingMainRect = find(cropPreviewAnnotations, annotation => {
                const data = annotation.getCustomData('cropPreview')
                const [component, parsedPageNumber] = data.split('-')
                return component === 'main' && parseInt(parsedPageNumber, 10) === pageNumber
              })

              // Create the main rectangle with transparent fill and red border
              const mainRect = (existingMainRect ??
                new Core.Annotations.RectangleAnnotation()) as any // eslint-disable-line @typescript-eslint/no-explicit-any
              mainRect.PageNumber = pageNumber
              mainRect.X = left
              mainRect.Y = top
              mainRect.Width = right - left
              mainRect.Height = bottom - top
              mainRect.StrokeThickness = 2
              mainRect.StrokeColor = new Core.Annotations.Color(255, 0, 0, 1) // Red border
              mainRect.FillColor = new Core.Annotations.Color(255, 255, 255, 0) // Transparent fill
              mainRect.Locked = true
              mainRect.ReadOnly = true
              mainRect.setCustomData('cropPreview', `main-${pageNumber}`)

              // Create red background rectangles for the areas that will be cropped
              const redColor = new Core.Annotations.Color(255, 0, 0, 0.15) // Semi-transparent red

              const existingTopRect = find(cropPreviewAnnotations, annotation => {
                const data = annotation.getCustomData('cropPreview')
                const [component, parsedPageNumber] = data.split('-')
                return component === 'top' && parseInt(parsedPageNumber, 10) === pageNumber
              })

              // Top area (above the crop rectangle)
              if (top > 0) {
                const topRect = (existingTopRect ??
                  new Core.Annotations.RectangleAnnotation()) as any // eslint-disable-line @typescript-eslint/no-explicit-any
                topRect.PageNumber = pageNumber
                topRect.X = 0
                topRect.Y = 0
                topRect.Width = pageWidth
                topRect.Height = top
                topRect.FillColor = redColor
                topRect.StrokeThickness = 0
                topRect.Locked = true
                topRect.ReadOnly = true
                topRect.setCustomData('cropPreview', `top-${pageNumber}`)
                if (existingTopRect) {
                  annotationManager.redrawAnnotation(topRect)
                } else {
                  annotationManager.addAnnotation(topRect)
                }
              } else {
                if (existingTopRect) {
                  annotationManager.deleteAnnotation(existingTopRect)
                }
              }

              const existingBottomRect = find(cropPreviewAnnotations, annotation => {
                const data = annotation.getCustomData('cropPreview')
                const [component, parsedPageNumber] = data.split('-')
                return component === 'bottom' && parseInt(parsedPageNumber, 10) === pageNumber
              })

              // Bottom area (below the crop rectangle)
              if (bottom < pageHeight) {
                const bottomRect = (existingBottomRect ??
                  new Core.Annotations.RectangleAnnotation()) as any // eslint-disable-line @typescript-eslint/no-explicit-any
                bottomRect.PageNumber = pageNumber
                bottomRect.X = 0
                bottomRect.Y = bottom
                bottomRect.Width = pageWidth
                bottomRect.Height = pageHeight - bottom
                bottomRect.FillColor = redColor
                bottomRect.StrokeThickness = 0
                bottomRect.Locked = true
                bottomRect.ReadOnly = true
                bottomRect.setCustomData('cropPreview', `bottom-${pageNumber}`)
                if (existingBottomRect) {
                  annotationManager.redrawAnnotation(bottomRect)
                } else {
                  annotationManager.addAnnotation(bottomRect)
                }
              } else {
                if (existingBottomRect) {
                  annotationManager.deleteAnnotation(existingBottomRect)
                }
              }

              const existingLeftRect = find(cropPreviewAnnotations, annotation => {
                const data = annotation.getCustomData('cropPreview')
                const [component, parsedPageNumber] = data.split('-')
                return component === 'left' && parseInt(parsedPageNumber, 10) === pageNumber
              })
              // Left area (left of the crop rectangle)
              if (left > 0) {
                const leftRect = (existingLeftRect ??
                  new Core.Annotations.RectangleAnnotation()) as any // eslint-disable-line @typescript-eslint/no-explicit-any
                leftRect.PageNumber = pageNumber
                leftRect.X = 0
                leftRect.Y = top
                leftRect.Width = left
                leftRect.Height = bottom - top
                leftRect.FillColor = redColor
                leftRect.StrokeThickness = 0
                leftRect.Locked = true
                leftRect.ReadOnly = true
                leftRect.setCustomData('cropPreview', `left-${pageNumber}`)
                if (existingLeftRect) {
                  annotationManager.redrawAnnotation(leftRect)
                } else {
                  annotationManager.addAnnotation(leftRect)
                }
              } else {
                if (existingLeftRect) {
                  annotationManager.deleteAnnotation(existingLeftRect)
                }
              }

              const existingRightRect = find(cropPreviewAnnotations, annotation => {
                const data = annotation.getCustomData('cropPreview')
                const [component, parsedPageNumber] = data.split('-')
                return component === 'right' && parseInt(parsedPageNumber, 10) === pageNumber
              })
              // Right area (right of the crop rectangle)
              if (right < pageWidth) {
                const rightRect = (existingRightRect ??
                  new Core.Annotations.RectangleAnnotation()) as any // eslint-disable-line @typescript-eslint/no-explicit-any
                rightRect.PageNumber = pageNumber
                rightRect.X = right
                rightRect.Y = top
                rightRect.Width = pageWidth - right
                rightRect.Height = bottom - top
                rightRect.FillColor = redColor
                rightRect.StrokeThickness = 0
                rightRect.Locked = true
                rightRect.ReadOnly = true
                rightRect.setCustomData('cropPreview', `right-${pageNumber}`)
                if (existingRightRect) {
                  annotationManager.redrawAnnotation(rightRect)
                } else {
                  annotationManager.addAnnotation(rightRect)
                }
              } else {
                if (existingRightRect) {
                  annotationManager.deleteAnnotation(existingRightRect)
                }
              }

              // Add the main rectangle last so it appears on top
              if (existingMainRect) {
                annotationManager.redrawAnnotation(mainRect)
              } else {
                annotationManager.addAnnotation(mainRect)
              }

              annotationManager.redrawAnnotation(mainRect)
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error creating crop preview:', error)
          }
        }
      },
      [viewerInstance],
    )

    useEffect(() => {
      if (!loadedDocumentUrl) return

      applyCroppedArea({
        croppedLeft: croppedLeft || 0,
        croppedTop: croppedTop || 0,
        croppedRight: croppedRight || 0,
        croppedBottom: croppedBottom || 0,
      })
    }, [loadedDocumentUrl, croppedLeft, croppedTop, croppedRight, croppedBottom, applyCroppedArea])

    // Primitive dependency, so a new array with the same pages doesn't redraw the overlays
    const excludedPagesKey = excludedPages?.join(',') ?? ''

    const documentGuid = document?.guid

    useEffect(() => {
      if (!loadedDocumentUrl || !viewerInstance) return

      // loadedDocumentUrl still points at the previous document while a new one loads
      const viewerDocument = viewerInstance.Core.documentViewer.getDocument()
      if (!viewerDocument || viewerDocument.getDocumentId() !== documentGuid) return

      applyPageExclusionPreview(
        viewerInstance.Core,
        excludedPagesKey ? excludedPagesKey.split(',').map(Number) : [],
      )
    }, [loadedDocumentUrl, viewerInstance, documentGuid, excludedPagesKey])

    useEffect(() => {
      if (!loadedDocumentUrl || !viewerInstance) return

      const viewerDocument = viewerInstance.Core.documentViewer.getDocument()

      if (!viewerDocument) return

      if (document?.watermark) {
        viewerInstance.Core.documentViewer.setWatermark(document.watermark, {})
      } else {
        viewerInstance.Core.documentViewer.setWatermark({}, {})
        viewerInstance.Core.documentViewer.refreshAll()
        viewerInstance.Core.documentViewer.updateView()
      }

      return () => {
        viewerInstance.Core.documentViewer.setWatermark({}, {})
      }
    }, [viewerInstance, document?.watermark, loadedDocumentUrl])

    useEffect(() => {
      if (!loadedDocumentUrl || !viewerInstance) return

      if (document?.editingDisabled) {
        viewerInstance.Core.annotationManager.enableReadOnlyMode()
      } else {
        viewerInstance.Core.annotationManager.disableReadOnlyMode()
      }

      return () => {
        viewerInstance.Core.annotationManager.disableReadOnlyMode()
      }
    }, [viewerInstance, document, loadedDocumentUrl])

    const documentRef = useLatest(document)

    useImperativeHandle<ViewerRef, ViewerRef>(
      ref as Ref<ViewerRef>,
      () => ({
        getBlobCurrentDocument: async () => {
          if (viewerInstance) {
            const xfdfString = await exportDocumentAnnotations(
              viewerInstance.Core.annotationManager,
            )
            const fileData = await viewerInstance.Core.documentViewer.getDocument().getFileData({
              includeAnnotations: true,
              xfdfString,
            })
            const blob = new Blob([fileData], { type: 'application/pdf' })

            return blob
          }
          return null
        },
        getBlobPDFA: async (
          pdfUlr: string,
          conformance: PDFAConformance,
        ): Promise<{ blob: Blob; errors: string[] } | null> => {
          if (viewerInstance) {
            await viewerInstance.Core.PDFNet.initialize(appConfig.apryseLicenceKey as string)

            const pdfa = await viewerInstance.Core.PDFNet.PDFACompliance.createFromUrl(
              true,
              pdfUlr,
              '',
              conformance,
              undefined,
              10,
            )
            const errorCount = await pdfa.getErrorCount()
            const errors: string[] = []
            if (errorCount > 0) {
              for (let i = 0; i < errorCount; i++) {
                const errorCode = await pdfa.getError(i)
                const errorMsg =
                  await viewerInstance.Core.PDFNet.PDFACompliance.getPDFAErrorMessage(errorCode)
                errors.push(errorMsg)
              }
            }

            const buf = await pdfa.saveAsFromBuffer(false)

            const blob = new Blob([buf] as BlobPart[], { type: 'application/pdf' })

            return { blob, errors }
          }
          return null
        },
        viewerInstance,
        reloadDocument: () => {
          const currentDocument = documentRef.current
          if (!viewerInstance || !currentDocument) return
          const extension = fileTypeToExtension[currentDocument.fileType]
          if (isUndefined(extension)) return
          // Annotation events while the document swaps are no user changes (see
          // `onAnnotationsChanged`).
          setDocumentLoading(true)
          const onReloaded = () => setDocumentLoading(false)
          viewerInstance.Core.documentViewer.addEventListener('annotationsLoaded', onReloaded, {
            once: true,
          })
          viewerInstance.UI.loadDocument(currentDocument.url, {
            filename: `${currentDocument.name}.${extension}`,
            extension,
            documentId: currentDocument.guid,
            // A failed reload (e.g. an expired signed url) must not leave the viewer loading
            // forever, which would also mute `onAnnotationsChanged` for every later edit.
            onError: loadError => {
              viewerInstance.Core.documentViewer.removeEventListener(
                'annotationsLoaded',
                onReloaded,
              )
              setError(
                typeof loadError === 'string' ? loadError : 'Fehler beim Laden des Dokuments',
              )
              setDocumentLoading(false)
            },
            enableOfficeEditing: !currentDocument.editingDisabled,
          })
        },
        // Stable per instance: a new handle each render would re-fire callback refs every commit
        // (e.g. the plan view's viewer context, which then re-renders the viewer — an update loop).
      }),
      [viewerInstance, documentRef, setDocumentLoading, setError],
    )

    const loadedDocumentUrlRef = useLatest(loadedDocumentUrl)

    useEffect(() => {
      if (!viewerInstance) return

      if (document) {
        setDocumentLoading(true)
        const currentDoc = viewerInstance.Core.documentViewer.getDocument()
        if (currentDoc) {
          const currentLoadedDocumentId = currentDoc.getDocumentId()
          if (
            currentLoadedDocumentId === document.guid &&
            loadedDocumentUrlRef.current === document.url
          ) {
            setDocumentLoading(false)
            return
          }
        }

        const documentLoadedEvent = () => {
          setDocumentLoading(false)
          setLoadedDocumentUrl(document.url)
          onDocumentLoaded?.(viewerInstance)
        }

        viewerInstance.Core.documentViewer.addEventListener(
          'annotationsLoaded',
          documentLoadedEvent,
        )

        const extension = fileTypeToExtension[document.fileType]

        if (isUndefined(extension)) {
          setError('Ungültiger Dokumenttyp')
          setDocumentLoading(false)
          return
        }

        viewerInstance.UI.loadDocument(document.url, {
          filename: `${document.name}.${extension}`,
          extension: extension,
          documentId: document.guid,
          onError: error => {
            setError(typeof error === 'string' ? error : 'Fehler beim Laden des Dokuments')
            setDocumentLoading(false)
          },
          enableOfficeEditing: !document.editingDisabled,
        })

        return () => {
          viewerInstance.Core.documentViewer.removeEventListener(
            'annotationsLoaded',
            documentLoadedEvent,
          )
        }
      } else {
        viewerInstance.UI.closeDocument()
      }
    }, [document, viewerInstance, loadedDocumentUrlRef, onDocumentLoaded])

    // ADD SHORTCUTS

    useEffect(() => {
      if (!viewerInstance) return

      const keyDownListener = (evt: KeyboardEvent) => {
        const bringToFrontAction = createBringToFrontAction(viewerInstance.Core.annotationManager)

        const bringToBackAction = createBringToBackAction(viewerInstance.Core.annotationManager)

        const copyEvents = createCopyEvents(viewerInstance.Core.annotationManager)

        if (evt.ctrlKey) {
          // CUSTOM EVENTS
          const shortcut = additionalShortcuts.find(shortcut => shortcut.key === evt.key)
          if (shortcut) {
            shortcut.action()
          }

          // COPY PASTE EVENTS

          copyEvents(evt)

          // Z Order related

          if (evt.key === 'w') {
            bringToFrontAction()
          }

          if (evt.key === 'b') {
            bringToBackAction()
          }
        }
      }

      viewerInstance.Core.documentViewer.addEventListener('keyDown', keyDownListener)

      return () => {
        viewerInstance.Core.documentViewer.removeEventListener('keyDown', keyDownListener)
      }
    }, [additionalShortcuts, viewerInstance])

    // HIDE DOWNLOAD / SAVE AS / PRINT

    useEffect(() => {
      if (!viewerInstance || !hideFileActions) return

      const { Feature } = viewerInstance.UI
      const features = [Feature.Download, Feature.Print]
      const dataElements = [
        'downloadButton',
        'downloadPresetButton',
        'saveAsButton',
        'saveAsPresetButton',
        'printButton',
        'printPresetButton',
      ]
      viewerInstance.UI.disableFeatures(features)
      viewerInstance.UI.disableElements(dataElements)

      return () => {
        viewerInstance.UI.enableFeatures(features)
        viewerInstance.UI.enableElements(dataElements)
      }
    }, [viewerInstance, hideFileActions])

    // ADD ANNOTATION POPUP

    useEffect(() => {
      if (!viewerInstance) return

      const originalItems = viewerInstance.UI.annotationPopup.getItems()

      const bringToFrontAction = createBringToFrontAction(viewerInstance.Core.annotationManager)

      const bringToBackAction = createBringToBackAction(viewerInstance.Core.annotationManager)

      viewerInstance.UI.annotationPopup.add([
        {
          type: 'actionButton',
          img: '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"/><rect x="6" y="6" width="12" height="12" fill="currentColor"/></svg>',
          title: 'Nach vorne',
          onClick: bringToFrontAction,
        },
        {
          type: 'actionButton',
          img: '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="12" height="12" fill="currentColor"/><rect x="6" y="6" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"/></svg>',
          title: 'Nach hinten',
          onClick: bringToBackAction,
        },
      ])

      return () => {
        viewerInstance.UI.annotationPopup.update(originalItems)
      }
    }, [viewerInstance])

    // ADD ANNOTATION CHANGE LISTENER

    const documentLoadingRef = useLatest(documentLoading)

    useEffect(() => {
      if (!viewerInstance) return

      const documentViewer = viewerInstance.Core.documentViewer

      const handleDocxChanged = () => {
        if (!documentRef.current?.editingDisabled) {
          onChangeDocument?.()
        }
      }

      documentViewer.addEventListener('keyDown', handleDocxChanged)
      documentViewer.addEventListener('click', handleDocxChanged)

      // Cleanup
      return () => {
        documentViewer.removeEventListener('keyDown', handleDocxChanged)
        documentViewer.removeEventListener('click', handleDocxChanged)
      }
    }, [viewerInstance, onChangeDocument, documentLoadingRef, documentRef])

    useEffect(() => {
      if (!viewerInstance || !onAnnotationsChanged) return

      const { annotationManager } = viewerInstance.Core
      const handleAnnotationChanged = (
        _annotations: unknown,
        _action: string,
        info: { imported?: boolean },
      ) => {
        // Annotations imported with a (re)loaded document are no user change.
        if (!info?.imported && !documentLoadingRef.current) onAnnotationsChanged()
      }

      annotationManager.addEventListener('annotationChanged', handleAnnotationChanged)
      return () => {
        annotationManager.removeEventListener('annotationChanged', handleAnnotationChanged)
      }
    }, [viewerInstance, onAnnotationsChanged, documentLoadingRef])

    // INSTANTIATE VIEWER

    const combinedError = error ?? viewerInstanceError

    const showViewer =
      viewerInstance && !documentLoading && !combinedError && !!document && !isLoadingViewerInstance

    return (
      <Box>
        <ViewerPlaceholders
          error={combinedError}
          documentSelected={!!document}
          isLoading={documentLoading || isLoadingViewerInstance}
          emptyState={emptyState}
        />

        <Box
          ref={ref => setViewerRef(current => current || (ref as HTMLDivElement))}
          sx={{
            height: showViewer ? viewerHeight : 0,
            width: '100%',
          }}
          visibility={showViewer ? 'visible' : 'hidden'}
          className="ph-no-capture"
        />
      </Box>
    )
  },
)

Viewer.displayName = 'Viewer'

export default Viewer
